import {
    IonIcon,
    IonProgressBar
} from "@ionic/react";

import {
    statsChartOutline
} from "ionicons/icons";

import {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    CompetitionGame,
    CompetitionTeam,
    Team,
    getCompetitionDetail
} from "../services/mexfbaApi";

// Ajusta esta ruta al archivo real de tu proyecto.
import {
    createStandingsByGroup,
    StandingRow
} from "../utils/competitionStandings";

interface GameStandingsProps {
    gameId: string;
    competitionId: string;
    gameDate?: string;
    localTeamId: string;
    visitorTeamId: string;
    teams: Team[];
    refreshToken: number;
}

/*
    Convierte una fecha YYYY-MM-DD a una clave comparable.

    La comparación se realiza por fecha de calendario, sin convertir
    la hora ni desplazar el día por zona horaria.
*/
function getDateKey(value?: string): string | null {
    const text = String(value || "").trim();

    const match = text.match(
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/
    );

    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    const date = new Date(Date.UTC(year, month - 1, day));

    if (
        date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day
    ) {
        return null;
    }

    return [
        String(year).padStart(4, "0"),
        String(month).padStart(2, "0"),
        String(day).padStart(2, "0")
    ].join("-");
}

function hasValidStandingScore(
    value: string | number | null | undefined
): boolean {
    if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
    ) {
        return false;
    }

    const score = Number(value);

    return Number.isInteger(score) && score >= 0;
}

/*
    La web usa códigos ASCII para los grupos:
    65 = A, 66 = B, etc.

    Si el JSON ya contiene "A", "B", etc., se conserva.
*/
function normalizeGroup(value?: string): string {
    const group = String(value || "").trim();

    if (/^\d+$/.test(group)) {
        const code = Number(group);

        if (code >= 65 && code <= 90) {
            return String.fromCharCode(code);
        }
    }

    return group;
}

function formatCutoffDate(value?: string): string {
    const key = getDateKey(value);

    if (!key) {
        return "";
    }

    const [year, month, day] = key.split("-").map(Number);

    // Fecha de calendario: no desplazarla según la zona del dispositivo.
    const date = new Date(year, month - 1, day, 12);

    return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "long",
        year: "numeric"
    }).format(date);
}

const GameStandings: React.FC<GameStandingsProps> = ({
    gameId,
    competitionId,
    gameDate,
    localTeamId,
    visitorTeamId,
    teams,
    refreshToken
}) => {
    const [standings, setStandings] =
        useState<Map<string, StandingRow[]> | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const teamsById = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [String(team.id), team];
            })
        );
    }, [teams]);

    useEffect(function () {
        let active = true;

        async function loadStandings(): Promise<void> {
            setLoading(true);
            setError("");
            setStandings(null);

            try {
                const cutoffDate = getDateKey(gameDate);

                if (!cutoffDate) {
                    throw new Error(
                        "El partido no tiene una fecha válida para calcular la tabla."
                    );
                }

                if (!competitionId) {
                    throw new Error(
                        "El partido no tiene una competencia identificada."
                    );
                }

                /*
                    Se fuerza la descarga al entrar o actualizar para no
                    depender de una copia anterior de la competencia.
                */
                const competition = await getCompetitionDetail(
                    competitionId,
                    true
                );

                if (!active) {
                    return;
                }

                /*
                    El JSON de competencia usa un objeto por equipos.
                    Si falta id dentro del valor, se usa su clave.
                */
                const competitionTeams: CompetitionTeam[] =
                    Object.entries(competition.Equipos || {}).map(
                        function ([key, team]) {
                            return {
                                ...team,
                                id: String(team.id || key),
                                nombre: team.nombre || "Equipo",
                                grupo: normalizeGroup(team.grupo)
                            };
                        }
                    );

                const competitionGames: CompetitionGame[] =
                    Object.entries(competition.Partidos || {}).map(
                        function ([key, game]) {
                            return {
                                ...game,
                                id: String(game.id || key)
                            };
                        }
                    );

                const gamesBeforeMatch = competitionGames.filter(
                    function (game) {
                        const date = getDateKey(game.fecha);

                        if (!date || date >= cutoffDate) {
                            return false;
                        }

                        if (String(game.id) === String(gameId)) {
                            return false;
                        }

                        /*
                            Si el partido trae id_comp, debe coincidir.
                            Si no lo trae, se asume que pertenece al archivo
                            de la competencia consultada.
                        */
                        if (
                            game.id_comp &&
                            String(game.id_comp) !== String(competitionId)
                        ) {
                            return false;
                        }

                        return (
                            hasValidStandingScore(game.resLocal) &&
                            hasValidStandingScore(game.resVisita)
                        );
                    }
                );

                /*
                    La función existente:
                    - Considera partidos de fase de grupos.
                    - Aplica los criterios de desempate de la app.
                    - Ignora equipos sin grupo.
                */
                const result = createStandingsByGroup(
                    competitionTeams,
                    gamesBeforeMatch
                );

                if (active) {
                    setStandings(result);
                }
            } catch (requestError) {
                console.error(
                    "No fue posible calcular las posiciones previas:",
                    requestError
                );

                if (active) {
                    setError(
                        requestError instanceof Error
                            ? requestError.message
                            : "No fue posible cargar las posiciones."
                    );
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }

        void loadStandings();

        return function () {
            active = false;
        };
    }, [
        gameId,
        competitionId,
        gameDate,
        refreshToken
    ]);

    const groups = standings
        ? Array.from(standings.entries()).sort(function (
            [firstGroup],
            [secondGroup]
        ) {
            return firstGroup.localeCompare(
                secondGroup,
                "es-MX",
                { numeric: true }
            );
        })
        : [];

    return (
        <section className="game-section">
            <div className="game-section__heading">
                <div>
                    <span>COMPETENCIA</span>
                    <h2>Posiciones antes del partido</h2>
                </div>

                <IonIcon icon={statsChartOutline} />
            </div>

            <div className="game-standings">
                {loading && <IonProgressBar />}

                {!loading && error && (
                    <p
                        className="game-standings__message"
                        role="status"
                    >
                        {error}
                    </p>
                )}

                {!loading && !error && (
                    <>
                        <p className="game-standings__note">
                            Resultados anteriores al{" "}
                            {formatCutoffDate(gameDate)}.
                            Los partidos de esa misma fecha no se incluyen.
                        </p>

                        {groups.length === 0 && (
                            <p className="game-standings__message">
                                No hay grupos disponibles para esta competencia.
                            </p>
                        )}

                        {groups.map(function ([groupName, rows]) {
                            return (
                                <div
                                    className="game-standings__group"
                                    key={groupName}
                                >
                                    <h3>Grupo {groupName}</h3>

                                    <div className="game-standings__scroll">
                                        <table className="game-standings__table">
                                            <caption>
                                                Posiciones del grupo {groupName}
                                                {" "}antes del partido
                                            </caption>

                                            <thead>
                                                <tr>
                                                    <th scope="col">#</th>
                                                    <th scope="col">Equipo</th>
                                                    <th scope="col">PJ</th>
                                                    <th scope="col">PG</th>
                                                    <th scope="col">PP</th>
                                                    <th scope="col">PE</th>
                                                    <th scope="col">PF</th>
                                                    <th scope="col">PC</th>
                                                    <th scope="col">DIF</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {rows.map(function (row) {
                                                    const isLocal =
                                                        String(row.id) ===
                                                        String(localTeamId);

                                                    const isVisitor =
                                                        String(row.id) ===
                                                        String(visitorTeamId);

                                                    const team = teamsById.get(
                                                        String(row.id)
                                                    );

                                                    const rowClass = isLocal
                                                        ? "game-standings__row--local"
                                                        : isVisitor
                                                            ? "game-standings__row--visitor"
                                                            : "";

                                                    return (
                                                        <tr
                                                            key={row.id}
                                                            className={rowClass}
                                                        >
                                                            <td>{row.position}</td>

                                                            <th scope="row">
                                                                <span className="game-standings__team">
                                                                    {team?.logo && (
                                                                        <img
                                                                            src={team.logo}
                                                                            alt=""
                                                                        />
                                                                    )}

                                                                    <span>
                                                                        {row.nombre}

                                                                        {isLocal && (
                                                                            <small>Local</small>
                                                                        )}

                                                                        {isVisitor && (
                                                                            <small>Visita</small>
                                                                        )}
                                                                    </span>
                                                                </span>
                                                            </th>

                                                            <td>{row.played}</td>
                                                            <td>{row.wins}</td>
                                                            <td>{row.losses}</td>
                                                            <td>{row.draws}</td>
                                                            <td>{row.pointsFor}</td>
                                                            <td>{row.pointsAgainst}</td>
                                                            <td>{row.difference}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </section>
    );
};

export default GameStandings;
